import type { IncomingMessage } from 'http'
import { isCustomDomainByHostname } from 'shared/utils/is-custom-domain'
import { getRequestHostInfo } from 'shared/utils/request-host'
import { BRAND_SETTINGS_INNER_API_URL, defaultBrandSettings } from '../constants/brand-settings'
import { BrandSettingsInput, BrandSettingsInterface } from '../types/brand-settings'
import { normalizeBrandSettings } from './normalize-brand-settings'

const REQUEST_TIMEOUT_MS = 1000

interface BrandAwareRequest extends IncomingMessage {
  __brandSettingsCache?: BrandSettingsInterface | null
  __brandSettingsResolved?: boolean
}

export const loadBrandSettingsServerSide = async (
  request?: IncomingMessage | null,
): Promise<BrandSettingsInterface | null> => {
  const req = request as BrandAwareRequest | undefined

  if (!req) {
    return defaultBrandSettings
  }

  if (req.__brandSettingsResolved) {
    return req.__brandSettingsCache ?? defaultBrandSettings
  }

  const { host } = getRequestHostInfo(req)

  if (!host || !isCustomDomainByHostname(host)) {
    req.__brandSettingsResolved = true
    req.__brandSettingsCache = defaultBrandSettings
    return req.__brandSettingsCache
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(
      `${process.env.SELF_HOST}${BRAND_SETTINGS_INNER_API_URL}?domain=${host}`,
      {
        method: 'GET',
        signal: controller.signal,
      },
    )

    if (!response.ok) {
      req.__brandSettingsResolved = true
      req.__brandSettingsCache = defaultBrandSettings
      return req.__brandSettingsCache
    }
    const payload = (await response.json()) as BrandSettingsInput

    const resolvedBrand = normalizeBrandSettings(payload ?? null, defaultBrandSettings)

    req.__brandSettingsCache = resolvedBrand
    req.__brandSettingsResolved = true
    return resolvedBrand
  } catch {
    req.__brandSettingsResolved = true
    req.__brandSettingsCache = defaultBrandSettings
    return defaultBrandSettings
  } finally {
    clearTimeout(timeoutId)
  }
}
